<template>
  <main class="wrapper-content">
    <div class="h3 p-2 bg-gray-50 shadow-md flex justify-between items-center flex-wrap">
      <div>
        <span class="hidden lg:inline mr-1">{{ $t('Program') }}</span>
        <span v-if="form.name" class="italic text-gray-400">'{{form.name}}'</span>
      </div>
      <a-button
        :disabled="!isOnEditMode"
        class="ml-2"
        @click="copyProgramToClipboard()"
      >
        <i class="mdi mdi-clipboard-text-outline text-lg transition-transform duration-300 ease-in-out group-hover:rotate-12"></i>
        <div class="hidden md:inline ml-1">Copiar programa</div>
      </a-button>
    </div>
    <template v-if="!isLoading">
      <b-card class="border">
        <b-row>
          <LmTextInput
            ref="nameInput"
            class="col-12 col-md-3"
            label="Nombre"
            v-model="$v.form.name.$model"
            :v="v('form.name')"
          />
          <LmDatePicker
            class="col-12 col-md-3"
            :showComponent="true"
            :config="{
              placeholder: dateFormat,
              dateFormat: dateFormat,
              allowInput: true
            }"
            label="Fecha de Desde"
            v-model="$v.form.startDate.$model"
            :v="v('form.startDate')"
          />
          <LmDatePicker
            class="col-12 col-md-3"
            :showComponent="true"
            :config="{
              placeholder: dateFormat,
              dateFormat: dateFormat,
              allowInput: true,
              minDate: minEndDate
            }"
            label="Fecha de Hasta"
            :disabled="form.startDate === null"
            v-model="$v.form.endDate.$model"
            :v="v('form.endDate')"
          />
          <LmTreeSelect
          class="col-12 col-md-3"
              :label="$t('Agency')"
              :options="availableAgencies"
              v-model="form.agency"
            />
        </b-row>
        <div class="mx-auto my-2">
          <div class="grid grid-cols-1 sm:grid-cols-4">
            <a-statistic :value="totalDays">
              <template #title>
                {{$t('total_days') }}
              </template>
            </a-statistic>
            <a-statistic :value="totalActivities">
              <template #title>
                {{$t('activities')}}
              </template>
            </a-statistic>
            <a-statistic :value="totalPassengers">
              <template #title>
                {{$t('total_passengers')}}
              </template>
            </a-statistic>

            <a-statistic :value="form.agency.name" v-if="form.agency">
              <template #title>
                {{$t('Agencia')}}
              </template>
              <template #suffix>
                <router-link :to="`/agencies/edit/${form.agency.id}`" class="text-blue-500 text-xl"><i class="mdi mdi-link"></i></router-link>
            </template>
            </a-statistic>
          </div>
        </div>
      </b-card>

      <a-collapse v-model="activeKey" expand-icon-position="right">
        <a-collapse-panel key="passengers" :header="passengerCollapsePanelTitle">
          <a-icon slot="extra" type="setting" />
          <LmDatatableWithModalSelectorModalCreator
            datatableSlug="passengers-internal-todos"
            datatableModalSlug="passengers-internal-todos"
            :hideOrdering="true"
            :hideTableTitle="true"
            :hideTableSearcher="true"
            :hideTableFilter="true"
            :hideTableButtons="false"
            :showAddButton="showButtonsOnPassengersTable"
            :showCreateButton="showButtonsOnPassengersTable"
            :buttonsDisabled="disableButtonsOnPassengersTable"
            :items="form.passengers"
            creatorFormComponent="Lumbretravel-LmPassenger"
            :creatorServiceEndpoint="creatorServiceEndpoint"
            :addItemButtonLabel="$t('add')"
            emptyItemsMessage="No hay un pasajero seleccionado"
            removeConfirmationMessage="Estas por quitar el pasajero, estas seguro?"
            @itemsAdded="onAddPassenger"
            @itemsToRemove="onRemovePassenger"
          />
          <div class="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-2">
            <!-- <a-button
              :disabled="!isOnEditMode"
              @click="showPassengersAssistantModal()"
            >
              <i class="mdi mdi-magic-staff text-lg transition-transform duration-300 ease-in-out group-hover:rotate-12 mr-1"></i>
              Carga de pasajeros con IA
            </a-button> -->
            <a-button
              :disabled="!isOnEditMode"
              @click="copyPassengersToClipboard()"
            >
              <i class="mdi mdi-clipboard-text-outline text-lg transition-transform duration-300 ease-in-out group-hover:rotate-12 mr-1"></i> Copiar pasajeros
            </a-button>
          </div>
        </a-collapse-panel>
        <a-collapse-panel key="activities" :header="activitiesCollapsePanelTitle">
          <a-icon slot="extra" type="setting" />
          <LmTableEditableRows
            :tableTitle="$t('activities')"
            :tableFooter="true"
            :columns="activitiesColumns"
            :initialItems="form.activities"
            :datePickerStartDate="datePickerStartDate"
            :datePickerEndDate="datePickerEndDate"
            :disableAddButton="form.passengers.length === 0"
            tableSize="default"
            timePickerSize="default"
            datePickerSize="default"
            :selectorOptions="selectorOptions"
            :activitiesBeingEdited="activitiesBeingEdited"
            :newEditingKey="editingKey"
            @updateItems="onUpdateActivities"
            @onAddActivity="onAddActivity"
            @setEditableOnActivity="setEditableOnActivity"
            @clearEditableOnActivity="clearEditableOnActivity"
            @activityEdited="handleActivityEdited"
            @activityRemoved="handleActivityRemoved"
            @activityCanceled="handleActivityCancelled"
          />
        </a-collapse-panel>
      </a-collapse>

      <div class="buttons">
        <LmButton
          buttonClass="btn-secondary"
          :value="$t('go_back')"
          @click="onGoBack"
          :isProcessing="isProcessing"
        />

        <a-tooltip :title="disabledWhenEditing">
        <LmButton
          buttonClass="btn-primary"
          :disabled="saveButtonDisabled"
          :value="$t('save')"
          @click="onSubmit(false)"
          :isProcessing="isProcessing"
        />
        </a-tooltip>

        <LmButton
          v-if="isOnEditMode && !form.deleted"
          :disabled="saveButtonDisabled"
          buttonClass="btn-danger"
          :value="$t('delete')"
          @click="onDeleteConfirmation"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && form.deleted"
          value="Reactivar"
          buttonClass="btn-success"
          @click="onReactivateConfirmation"
          :isProcessing="isProcessing"
        />

      </div>
    </template>
    <b-row v-else>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col md="12" class="my-4">
        <div>
          <div class="d-flex flex-wrap mb-3" style="gap: 1rem">
            <span
              class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
              style="gap: 1rem"
              v-for="(item, index) in 4"
              :key="index"
            >
              <b-skeleton type="avatar"></b-skeleton>
              <b-skeleton width="100px"></b-skeleton>
            </span>
          </div>
          <b-skeleton type="button" width="150px"></b-skeleton>
        </div>
      </b-col>
    </b-row>
    <b-modal
      id="passengers-assistant-modal"
      no-close-on-backdrop
      :title="$t('Passengers creator assitant')"
      size="xl"
    >
      <LmPassengerWizard
        @onClose="closePassengersAssistantModal"
        @onPassengersCreated="onPassengersCreatedWizard"
        :creatorServiceEndpoint="creatorServiceEndpoint"
      />
    </b-modal>
  </main>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2.vue'
import LmDatePicker from '@/components/form/elements/LmDatePicker'
import LmUploadFiles from '@/components/form/elements/LmUploadFiles'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmDatatableWithModalSelectorModalCreator from '@/components/LmDatatableWithModalSelectorModalCreator'
import LmFileViewer from '@/components/LmFileViewer'
import LmTableEditableRows from '@/components/LmTableEditableRows'
import LmPassengerWizard from '@/components/LmPassengerWizard'

// Mixins
import lmStandardFormHelperMixin from '@/mixins/lmStandardFormHelperMixin'

// Utils
import commonsApi from '@/api/commons'

import { required } from 'vuelidate/lib/validators'
import moment from 'moment'

import LmTextInput from '../../components/form/elements/LmTextInput.vue'

export default {
  mixins: [lmStandardFormHelperMixin],
  components: {
    LmUploadFiles,
    LmDatePicker,
    LmTextInput,
    LmNumberInputV2,
    LmTreeSelect,
    LmDatatableWithModalSelectorModalCreator,
    LmFileViewer,
    LmButton,
    LmTableEditableRows,
    LmPassengerWizard
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    creatorServiceEndpoint: {
      type: String
    },
    passengerEditRoute: {
      type: String
    },
    servicesEditRoute: {
      type: String
    },
    hotelsEditRoute: {
      type: String
    },
    languageServicesEditRoute: {
      type: String
    },
    includesEditRoute: {
      type: String
    },
    leadersEditRoute: {
      type: String
    },
    vehiclesEditRoute: {
      type: String
    },
    servicesAllItemsEndpoint: {
      type: String
    },
    hotelsAllItemsEndpoint: {
      type: String
    },
    vehiclesAllItemsEndpoint: {
      type: String
    },
    leadersAllItemsEndpoint: {
      type: String
    },
    agenciesAllItemsEndpoint: {
      type: String
    },
    serviceLanguagesAllItemsEndpoint: {
      type: String
    },
    includesAllItemsEndpoint: {
      type: String
    },
    frontendRouteToRedirectAfterCreated: {
      type: String,
      required: true
    },
    dailyTargetLink: {
      type: String,
      required: true
    },
    serviceEndpoint: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      activitiesColumns: [
        {
          title: this.$t('Passengers'),
          scopedSlots: { customRender: 'passengers' },
          dataIndex: 'passengers',
          fieldType: 'multiselect',
          width: '15%',
          includeInMainRow: true,
          targetLink: this.passengerEditRoute
        },
        {
          title: this.$t('Date'),
          scopedSlots: { customRender: 'date' },
          dataIndex: 'date',
          fieldType: 'datepicker',
          width: '15%',
          // sorter must sort a date object, not a string
          sorter: (a, b) => moment(`${a.date} ${a.hour}`, this.dateTimeFormat).diff(moment(`${b.date} ${b.hour}`, this.dateTimeFormat)),
          // defaultSortOrder: 'ascend',
          includeInMainRow: true,
          dailyTargetLink: this.dailyTargetLink
        },
        {
          title: this.$t('Hour'),
          scopedSlots: { customRender: 'hour' },
          dataIndex: 'hour',
          fieldType: 'timepicker-mask',
          width: '8%',
          includeInMainRow: true
        },
        {
          title: this.$t('Service'),
          scopedSlots: { customRender: 'service' },
          dataIndex: 'service',
          fieldType: 'singleselect',
          fieldTagColor: 'green',
          width: '12%',
          includeInMainRow: true,
          targetLink: this.servicesEditRoute
        },
        {
          title: this.$t('Hotel'),
          scopedSlots: { customRender: 'hotel' },
          dataIndex: 'hotel',
          fieldType: 'singleselect',
          fieldTagColor: 'pink',
          width: '12%',
          includeInMainRow: true,
          targetLink: this.hotelsEditRoute
        },
        {
          title: this.$t('Leader'),
          scopedSlots: { customRender: 'leader' },
          dataIndex: 'leader',
          fieldType: 'singleselect',
          fieldTagColor: 'blue',
          width: '12%',
          includeInMainRow: true,
          targetLink: this.leadersEditRoute
        },
        {
          title: this.$t('Vehicle'),
          scopedSlots: { customRender: 'vehicle' },
          dataIndex: 'vehicle',
          fieldType: 'singleselect',
          fieldTagColor: 'orange',
          width: '12%',
          includeInMainRow: true,
          targetLink: this.vehiclesEditRoute
        },
        {
          title: this.$t('Code'),
          scopedSlots: { customRender: 'code' },
          dataIndex: 'code',
          sorter: (a, b) => a.code.localeCompare(b.code),
          includeInExpandableRow: true
        },
        {
          title: this.$t('Itinerary'),
          scopedSlots: { customRender: 'itinerary' },
          dataIndex: 'itinerary',
          includeInExpandableRow: true
        },
        {
          title: this.$t('Includes'),
          scopedSlots: { customRender: 'includes' },
          dataIndex: 'includes',
          fieldType: 'multiselect',
          width: '15%',
          includeInExpandableRow: true,
          targetLink: this.includesEditRoute
        },
        {
          title: this.$t('Sevice Language'),
          scopedSlots: { customRender: 'servicelanguage' },
          dataIndex: 'servicelanguage',
          fieldType: 'singleselect',
          fieldTagColor: 'blue',
          width: '12%',
          includeInExpandableRow: true,
          targetLink: this.languageServicesEditRoute
        },
        {
          title: this.$t('News'),
          scopedSlots: { customRender: 'news' },
          dataIndex: 'news',
          includeInExpandableRow: true
        },
        {
          title: this.$t('actions'),
          scopedSlots: { customRender: 'actionSlot' },
          dataIndex: 'actions',
          includeInMainRow: true
        }
      ],
      editingKey: '',
      activeKey: [],
      isLoading: false,
      dateFormat: 'DD-MM-YYYY',
      dateTimeFormat: 'DD-MM-YYYY HH:mm',
      timeFormat: 'HH:mm',
      activitiesBeingEdited: 0,
      availableServices: [],
      availableHotels: [],
      availableVehicles: [],
      availableLeaders: [],
      availableAgencies: [],
      availableServiceLanguage: [],
      availableIncludes: [],
      form: {
        name: '',
        startDate: null,
        endDate: null,
        passengers: [],
        deleted: false,
        agency: null,
        activities: []
      },
      isProcessing: true
    }
  },
  validations: {
    form: {
      name: { required },
      startDate: { required },
      endDate: { required }
    }
  },
  computed: {
    showButtonsOnPassengersTable () {
      return (!this.$v.form.$invalid && !this.$v.form.$error.required)
    },
    disableButtonsOnPassengersTable () {
      return this.activitiesBeingEdited > 0
    },
    disabledWhenEditing () {
      return this.activitiesBeingEdited > 0 ? 'Para poder guardar, no deben haber actividades siendo editadas.' : null
    },
    activitiesCollapsePanelTitle () {
      if (this.activeKey.includes('activities')) {
        return `${this.$t('activities')}`
      } else {
        return `${this.$t('activities')}: ${this.totalActivities}`
      }
    },
    saveButtonDisabled () {
      return this.form.startDate === null || this.form.endDate === null || this.form.name === '' || this.activitiesBeingEdited > 0
    },
    passengerCollapsePanelTitle () {
      return `${this.$t('passengers_program')}: ${this.totalPassengers}`
    },
    totalPassengers () {
      return this.form.passengers.length
    },
    totalActivityPassengers () {
      return this.form.activities.reduce((acc, curr) => acc + curr.passengers.length, 0)
    },
    totalActivities () {
      return this.form.activities.length
    },
    totalDays () {
      if (this.form.startDate && this.form.endDate) {
        const startDate = moment(this.form.startDate, this.dateFormat)
        const endDate = moment(this.form.endDate, this.dateFormat)
        return endDate.diff(startDate, 'days') + 1
      } else {
        return 0
      }
    },
    minEndDate () {
      if (this.form.startDate) {
        return moment(this.form.startDate, this.dateFormat).add(-1, 'day').toDate()
      } else {
        return null
      }
    },
    datePickerStartDate () {
      return this.form.startDate !== null ? moment(this.form.startDate, this.dateFormat).toDate() : null
    },
    datePickerEndDate () {
      return this.form.endDate !== null ? moment(this.form.endDate, this.dateFormat).toDate() : null
    },
    selectorOptions () {
      return {
        passengers: this.form.passengers.map(p => {
          return {
            key: p.id,
            label: p.name,
            icon: p.icon
          }
        }),
        service: this.availableServices,
        hotel: this.availableHotels,
        vehicle: this.availableVehicles,
        leader: this.availableLeaders,
        servicelanguage: this.availableServiceLanguage,
        includes: this.availableIncludes
      }
    }
  },
  methods: {
    async copyPassengersToClipboard () {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/passengers_in_program_clipboard`, {
        id: this.$route.params.id
      })

      const htmlContent = res.data

      // Crear un elemento de contenedor temporal
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      tempDiv.style.position = 'absolute'
      tempDiv.style.left = '-9999px' // Ubicar fuera de la pantalla para que no se vea
      document.body.appendChild(tempDiv)

      // Seleccionar el contenido del contenedor temporal
      const range = document.createRange()
      range.selectNodeContents(tempDiv)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)

      try {
        // Intentar copiar el contenido seleccionado al portapapeles
        const successful = document.execCommand('copy')
        if (successful) {
          this.lmToastSuccessMessage(
            'Detalles de los pasajeros copiados al portapapeles',
            `${this.$t('action_done_successfully')}`
          )
        } else {
          alert('No se pudo copiar el contenido')
        }
      } catch (err) {
        console.error('Error al copiar el contenido:', err)
      }

      // Limpiar la selección y remover el contenedor temporal
      selection.removeAllRanges()
      document.body.removeChild(tempDiv)
    },
    async copyProgramToClipboard () {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}/program_clipboard`, {
        id: this.$route.params.id
      })

      const htmlContent = res.data

      // Crear un elemento de contenedor temporal
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      tempDiv.style.position = 'absolute'
      tempDiv.style.left = '-9999px' // Ubicar fuera de la pantalla para que no se vea
      document.body.appendChild(tempDiv)

      // Seleccionar el contenido del contenedor temporal
      const range = document.createRange()
      range.selectNodeContents(tempDiv)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)

      try {
        // Intentar copiar el contenido seleccionado al portapapeles
        const successful = document.execCommand('copy')
        if (successful) {
          this.lmToastSuccessMessage(
            'Detalles de programa copiados al portapapeles',
            `${this.$t('action_done_successfully')}`
          )
        } else {
          alert('No se pudo copiar el contenido')
        }
      } catch (err) {
        console.error('Error al copiar el contenido:', err)
      }

      // Limpiar la selección y remover el contenedor temporal
      selection.removeAllRanges()
      document.body.removeChild(tempDiv)
    },
    async showPassengersAssistantModal () {
      this.$bvModal.show('passengers-assistant-modal')
    },
    async onPassengersCreatedWizard (passengers) {
      await this.loadData()
      this.closePassengersAssistantModal()

      let message = `Se agregaron '${passengers.length}' pasajeros al programa '${this.form.name}'`
      if (passengers.length === 1) {
        message = `Se agregó '${passengers.length}' pasajero al programa '${this.form.name}'`
      }
      this.lmToastSuccessMessage(
        message,
        `${this.$t('action_done_successfully')}`
      )
    },
    closePassengersAssistantModal () {
      this.$bvModal.hide('passengers-assistant-modal')
    },
    handleActivityEdited () {
      this.activitiesBeingEdited++
      this.hasChanged = true
    },
    handleActivityRemoved () {
      this.activitiesBeingEdited--
      this.hasChanged = true
    },
    handleActivityCancelled () {
      this.activitiesBeingEdited--
    },
    forceFocus () {
      this.$nextTick(() => {
        this.$refs.nameInput.forceFocus()
      })
    },
    defaultActiveKey () {
      let activeKeys = ['activities', 'passengers']
      // if (this.form.passengers.length === 0) { activeKeys.push('passengers') }
      return activeKeys
    },
    onAddActivity () {
      const now = new Date()
      const emptyActivity = {
        // quiero castear a string key activities.lengt
        key: this.form.activities.length.toString(),
        passengers: [],
        date: this.form.startDate || null,
        hour: moment(now).format(this.timeFormat),
        hotel: '',
        service: '',
        code: '',
        includes: [],
        itinerary: '',
        vehicle: '',
        leader: '',
        servicelanguage: '',
        news: '',
        editable: true
      }
      this.editingKey = emptyActivity.key
      this.form.activities.push(emptyActivity)
      this.activitiesBeingEdited++
      this.hasChanged = true
    },
    setEditableOnActivity (_id) {
      const activity = this.form.activities.find(a => a._id === _id)
      if (activity) {
        activity.editable = true
      }
    },
    clearEditableOnActivity (key) {
      const activity = this.form.activities.find(a => a.key === key)
      if (activity) {
        delete activity.editable
      }
    },
    onUpdateActivities (items) {
      this.form.activities = [...items]
      this.onSubmit(false)
    },

    onAddPassenger (items) {
      if (items?.length) {
        this.form.passengers.push(...items.map(i => {
          return {
            id: i.id,
            name: `${i['passengers-lastname']}, ${i['passengers-firstname']}`
          }
        }))
        // this.onSubmit(false)
      }
    },

    async onRemovePassenger (items) {
      try {
        if (items?.length) {
          const idsToRemove = items.map(i => i.id)
          // Validate that passengers to remove are not in any activities
          const passengersCantBeRemoved = this.form.activities.some(activity => activity.passengers.some(p => idsToRemove.includes(p)))

          if (passengersCantBeRemoved) {
            throw new Error('validation error')
          } else {
            this.form.passengers = this.form.passengers.filter(obj => !idsToRemove.includes(obj.id))
            // this.onSubmit(false)
          }
        }
      } catch (error) {
        await this.$nextTick()
        this.lmToastDangerMessage(this.$t('passengers_found_in_activities'), 'Error')
      }
    },
    async loadData () {
      const res = await commonsApi.axiosGetWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`)
      this.form = res.data
      this.activeKey = this.defaultActiveKey()
    },
    async loadMasterData () {
      try {
        const results = await Promise.allSettled([
          commonsApi.axiosGetWrapper(this.servicesAllItemsEndpoint),
          commonsApi.axiosGetWrapper(this.hotelsAllItemsEndpoint),
          commonsApi.axiosGetWrapper(this.vehiclesAllItemsEndpoint),
          commonsApi.axiosGetWrapper(this.leadersAllItemsEndpoint),
          commonsApi.axiosGetWrapper(this.agenciesAllItemsEndpoint),
          commonsApi.axiosGetWrapper(this.serviceLanguagesAllItemsEndpoint),
          commonsApi.axiosGetWrapper(this.includesAllItemsEndpoint)
        ])

        let index = 0
        for (const result of results) {
          if (result.status === 'fulfilled') {
            const resultData = result.value.data.items.map(s => ({ key: s.id, label: s.name }))
            if (index === 0) {
              Object.assign(this.availableServices, resultData)
            } else if (index === 1) {
              Object.assign(this.availableHotels, resultData)
            } else if (index === 2) {
              Object.assign(this.availableVehicles, resultData)
            } else if (index === 3) {
              Object.assign(this.availableLeaders, resultData)
            } else if (index === 4) {
              // Es un caso especial de agencias, se necesita mapear el id y el name
              this.availableAgencies = resultData.map(s => ({ id: s.key, name: s.label }))
            } else if (index === 5) {
              Object.assign(this.availableServiceLanguage, resultData)
            } else if (index === 6) {
              Object.assign(this.availableIncludes, resultData)
            }
          }
          index++
        }
      } catch (error) {
        console.log(error)
      }
    },

    async onSubmit (finishWithGoBack = true) {
      try {
        await this.validateForm(this.$v)
        this.isProcessing = true
        this.form.issueDate = moment(this.form.issueDate, this.dateFormat).toDate()

        let res
        if (this.isOnEditMode) {
          await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`, this.form)
        } else {
          res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}`, this.form)
        }
        this.hasChanged = false
        this.lmToastSuccessMessage(
          `${this.$t('saved_successfully')} '${this.form.name}'`,
          `${this.$t('action_done_successfully')}`
        )

        if (this.isOnModal) {
          this.$emit('itemCreated', {
            ...res.data,
            id: res.data._id
          })
          this.onClose()
        } else {
          if (finishWithGoBack) {
            this.onGoBack()
          } else if (!this.isOnEditMode) {
            if (this.frontendRouteToRedirectAfterCreated) {
              this.$router.replace(`${this.frontendRouteToRedirectAfterCreated}${res.data._id}`)
            } else {
              this.onGoBack()
            }
          }
        }
      } catch (error) {
        console.log(error)
        throw (error)
      } finally {
        this.isProcessing = false
        this.hasChanged = false
        this.activitiesBeingEdited = 0
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      await this.loadMasterData()
      if (!this.isOnEditMode) {
        this.activeKey = this.defaultActiveKey()
      }
      this.isProcessing = false
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
      // this.forceFocus()
    }
  }
}
</script>
